import React, { useState, useEffect } from "react";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  query,
  orderBy,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import {
  FaCheck,
  FaTimes,
  FaUser,
  FaBuilding,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaClock,
  FaEye,
  FaTrashAlt,
  FaWhatsapp,
} from "react-icons/fa";
import { Modal, Button } from "react-bootstrap";

export default function UserRegistrations() {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, pending, approved, rejected
  const [actionLoading, setActionLoading] = useState(null); // uid of the user being actioned
  const [selectedUser, setSelectedUser] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const fetchRegistrations = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, "userRegistrations"), orderBy("createdAt", "desc"));
      const snap = await getDocs(q);
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setRegistrations(data);
    } catch (err) {
      console.error("Error fetching registrations:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRegistrations();
  }, []);

  const handleAction = async (uid, newStatus) => {
    setActionLoading(uid);
    try {
      await updateDoc(doc(db, "userRegistrations", uid), {
        status: newStatus,
        updatedAt: serverTimestamp(),
      });
      // Update local state
      setRegistrations((prev) =>
        prev.map((r) => (r.id === uid ? { ...r, status: newStatus } : r))
      );
      if (selectedUser?.id === uid) {
        setSelectedUser((prev) => ({ ...prev, status: newStatus }));
      }
    } catch (err) {
      console.error("Error updating status:", err);
      alert("Failed to update status. Please try again.");
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (uid) => {
    if (!window.confirm("Are you sure you want to delete this user registration? This action cannot be undone.")) {
      return;
    }
    setActionLoading(uid);
    try {
      await deleteDoc(doc(db, "userRegistrations", uid));
      setRegistrations((prev) => prev.filter((r) => r.id !== uid));
      if (selectedUser?.id === uid) {
        setShowViewModal(false);
        setSelectedUser(null);
      }
    } catch (err) {
      console.error("Error deleting registration:", err);
      alert("Failed to delete user. Please try again.");
    } finally {
      setActionLoading(null);
    }
  };

  const filtered = registrations.filter((r) => {
    if (filter !== "all" && r.status !== filter) return false;
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      r.name?.toLowerCase().includes(q) ||
      r.email?.toLowerCase().includes(q) ||
      r.company?.toLowerCase().includes(q) ||
      r.phone?.toLowerCase().includes(q) ||
      r.city?.toLowerCase().includes(q)
    );
  });

  const counts = {
    all: registrations.length,
    pending: registrations.filter((r) => r.status === "pending").length,
    approved: registrations.filter((r) => r.status === "approved").length,
    rejected: registrations.filter((r) => r.status === "rejected").length,
  };

  const formatDate = (ts) => {
    if (!ts) return "—";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div>
      <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between mb-4 gap-3">
        <div>
          <h4 className="fw-bold mb-1 text-dark" style={{ color: "#0F172A" }}>
            <FaUser className="me-2 text-danger" />
            User Registrations
          </h4>
          <p className="text-muted mb-0 small">
            Manage dealer registration requests, approve accounts, or view user details
          </p>
        </div>
      </div>

      <div className="admin-card">
        {/* Filter Tabs Header */}
        <div className="admin-card-header d-flex flex-wrap align-items-center justify-content-between gap-3 bg-white">
          <div className="d-flex flex-wrap align-items-center gap-2">
            {["all", "pending", "approved", "rejected"].map((f) => (
              <button
                key={f}
                className={`btn btn-sm rounded-pill px-3 py-1 fw-semibold ${
                  filter === f
                    ? f === "pending"
                      ? "btn-warning text-dark"
                      : f === "approved"
                      ? "btn-success text-white"
                      : f === "rejected"
                      ? "btn-danger text-white"
                      : "btn-dark text-white"
                    : "btn-outline-secondary"
                }`}
                onClick={() => setFilter(f)}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)} ({counts[f]})
              </button>
            ))}
          </div>

          <div className="d-flex align-items-center gap-3 ms-auto">
            <input
              type="text"
              placeholder="Search user name, email, company, city..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="form-control form-control-sm py-1.5 px-3 rounded-pill"
              style={{ fontSize: '13px', width: '280px' }}
            />
            <div className="text-muted small fw-medium text-nowrap">
              Total Users: <strong>{filtered.length}</strong>
            </div>
          </div>
        </div>

        <div className="admin-table-container">
          <table className="admin-table table-hover align-middle">
            <thead>
              <tr>
                <th>User</th>
                <th>Contact</th>
                <th>Company</th>
                <th>Date</th>
                <th>Status</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="text-center py-5 text-muted">
                    <p className="fw-medium mb-0">Loading user registrations...</p>
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-5 text-muted">
                    <p className="fw-medium mb-0">No user registrations found matching query.</p>
                  </td>
                </tr>
              ) : (
                filtered.map((reg) => (
                  <tr key={reg.id}>
                    <td>
                      <div className="fw-semibold text-dark">{reg.name}</div>
                      <div className="text-muted small">
                        <FaEnvelope className="me-1 text-secondary" size={11} />
                        {reg.email}
                      </div>
                    </td>
                    <td>
                      <div className="small text-dark">
                        <FaPhone className="me-1 text-secondary" size={11} />
                        {reg.phone}
                      </div>
                    </td>
                    <td>
                      <div className="small fw-medium text-dark">
                        <FaBuilding className="me-1 text-secondary" size={11} />
                        {reg.company}
                      </div>
                      <div className="text-muted small">
                        <FaMapMarkerAlt className="me-1 text-secondary" size={11} />
                        {reg.city}
                      </div>
                    </td>
                    <td>
                      <div className="small text-muted">
                        <FaClock className="me-1" size={11} />
                        {formatDate(reg.createdAt)}
                      </div>
                    </td>
                    <td>
                      <span
                        className={`badge rounded-pill px-3 py-2 fw-semibold ${
                          reg.status === "approved"
                            ? "bg-success"
                            : reg.status === "rejected"
                            ? "bg-danger"
                            : "bg-warning text-dark"
                        }`}
                      >
                        {reg.status.charAt(0).toUpperCase() + reg.status.slice(1)}
                      </span>
                    </td>
                    <td className="text-center">
                      <div className="admin-action-btns">
                        {/* View Button */}
                        <button
                          className="btn-action btn-action-view"
                          title="View Details"
                          onClick={() => {
                            setSelectedUser(reg);
                            setShowViewModal(true);
                          }}
                        >
                          <FaEye size={14} />
                        </button>

                        {/* Approve / Reject / Revoke Buttons */}
                        {reg.status === "pending" && (
                          <>
                            <button
                              className="btn btn-sm btn-success rounded-pill px-3 d-flex align-items-center gap-1"
                              style={{ fontSize: "12px" }}
                              onClick={() => handleAction(reg.id, "approved")}
                              disabled={actionLoading === reg.id}
                            >
                              <FaCheck size={11} /> Approve
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger rounded-pill px-3 d-flex align-items-center gap-1"
                              style={{ fontSize: "12px" }}
                              onClick={() => handleAction(reg.id, "rejected")}
                              disabled={actionLoading === reg.id}
                            >
                              <FaTimes size={11} /> Reject
                            </button>
                          </>
                        )}

                        {reg.status === "rejected" && (
                          <button
                            className="btn btn-sm btn-outline-success rounded-pill px-3 d-flex align-items-center gap-1"
                            style={{ fontSize: "12px" }}
                            onClick={() => handleAction(reg.id, "approved")}
                            disabled={actionLoading === reg.id}
                          >
                            <FaCheck size={11} /> Approve
                          </button>
                        )}

                        {reg.status === "approved" && (
                          <button
                            className="btn btn-sm btn-outline-warning rounded-pill px-3 d-flex align-items-center gap-1"
                            style={{ fontSize: "12px" }}
                            onClick={() => handleAction(reg.id, "rejected")}
                            disabled={actionLoading === reg.id}
                          >
                            <FaTimes size={11} /> Revoke
                          </button>
                        )}

                        {/* Delete Button */}
                        <button
                          className="btn-action btn-action-delete"
                          title="Delete User"
                          onClick={() => handleDelete(reg.id)}
                          disabled={actionLoading === reg.id}
                        >
                          <FaTrashAlt size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* View User Details Modal */}
      <Modal show={showViewModal} onHide={() => setShowViewModal(false)} centered size="md">
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold fs-5 d-flex align-items-center gap-2">
            <FaUser className="text-danger" /> User Registration Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-3">
          {selectedUser && (
            <div>
              <div className="d-flex align-items-center justify-content-between p-3 bg-light rounded-3 mb-3 border">
                <div>
                  <h5 className="fw-bold mb-0 text-dark">{selectedUser.name}</h5>
                  <small className="text-muted">{selectedUser.company}</small>
                </div>
                <span
                  className={`badge rounded-pill px-3 py-2 fs-6 ${
                    selectedUser.status === "approved"
                      ? "bg-success"
                      : selectedUser.status === "rejected"
                      ? "bg-danger"
                      : "bg-warning text-dark"
                  }`}
                >
                  {selectedUser.status?.toUpperCase()}
                </span>
              </div>

              <div className="list-group list-group-flush mb-3">
                <div className="list-group-item d-flex align-items-center justify-content-between px-0 py-2">
                  <span className="text-muted small"><FaEnvelope className="me-2 text-secondary" /> Email</span>
                  <a href={`mailto:${selectedUser.email}`} className="fw-semibold text-decoration-none text-dark small">
                    {selectedUser.email}
                  </a>
                </div>

                <div className="list-group-item d-flex align-items-center justify-content-between px-0 py-2">
                  <span className="text-muted small"><FaPhone className="me-2 text-secondary" /> Phone</span>
                  <div className="d-flex align-items-center gap-2">
                    <a href={`tel:${selectedUser.phone}`} className="fw-semibold text-decoration-none text-dark small">
                      {selectedUser.phone}
                    </a>
                    {selectedUser.phone && (
                      <a
                        href={`https://wa.me/91${selectedUser.phone.replace(/[^0-9]/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-success py-0 px-2 rounded-pill"
                        style={{ fontSize: '11px' }}
                      >
                        <FaWhatsapp className="me-1" /> WhatsApp
                      </a>
                    )}
                  </div>
                </div>

                <div className="list-group-item d-flex align-items-center justify-content-between px-0 py-2">
                  <span className="text-muted small"><FaBuilding className="me-2 text-secondary" /> Company / Shop</span>
                  <span className="fw-semibold text-dark small">{selectedUser.company || "—"}</span>
                </div>

                <div className="list-group-item d-flex align-items-center justify-content-between px-0 py-2">
                  <span className="text-muted small"><FaMapMarkerAlt className="me-2 text-secondary" /> City</span>
                  <span className="fw-semibold text-dark small">{selectedUser.city || "—"}</span>
                </div>

                <div className="list-group-item d-flex align-items-center justify-content-between px-0 py-2">
                  <span className="text-muted small"><FaClock className="me-2 text-secondary" /> Registration Date</span>
                  <span className="fw-semibold text-dark small">{formatDate(selectedUser.createdAt)}</span>
                </div>

                <div className="list-group-item d-flex align-items-center justify-content-between px-0 py-2">
                  <span className="text-muted small">User ID</span>
                  <span className="text-muted small font-monospace">{selectedUser.id}</span>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 bg-light d-flex justify-content-between">
          <Button variant="outline-danger" size="sm" className="d-flex align-items-center gap-1" onClick={() => handleDelete(selectedUser?.id)}>
            <FaTrashAlt size={12} /> Delete User
          </Button>
          <div className="d-flex gap-2">
            {selectedUser?.status !== "approved" && (
              <Button variant="success" size="sm" onClick={() => { handleAction(selectedUser.id, "approved"); }}>
                <FaCheck size={12} className="me-1" /> Approve
              </Button>
            )}
            {selectedUser?.status !== "rejected" && (
              <Button variant="warning" size="sm" className="text-dark" onClick={() => { handleAction(selectedUser.id, "rejected"); }}>
                <FaTimes size={12} className="me-1" /> Reject
              </Button>
            )}
            <Button variant="secondary" size="sm" onClick={() => setShowViewModal(false)}>
              Close
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
